/**
 * API client setup — uses DryStuff's native features.
 *
 * - DryConnect: HTTP client with retry, offline queue, FormData support
 * - DryAuth: Token management with getToken() for raw JWT strings
 * - DryStore: Cache-through interceptors for offline reads
 * - DryConnect WebSocket: Real-time notifications
 */

import { createClient, createNetworkMonitor, createWebSocket } from '@drystuff/connect';
import { createAuthManager, LocalTokenStorage } from '@drystuff/auth';
import { createCachingInterceptor, createOfflineFallbackInterceptor } from '@drystuff/store';
import type { CacheMapping } from '@drystuff/store';
import { config } from './config';
import { createNotificationManager } from '@drystuff/notify';
import type { NotificationManager } from '@drystuff/notify';
// Offline-unlock refresh flow. Imported from the SPECIFIC module files (not the
// secure/ barrel) to keep this module's import graph minimal and avoid pulling
// the whole barrel into the client at init.
import { refreshAccessTokenWith } from './secure/reconnect';
import { createSecureVault } from './secure/secureVault';
import { getUnlockedRefreshToken } from './secure/unlockBridge';

// ─── HTTP Client (DryConnect) ───
export const http = createClient({
  baseUrl: config.api.baseUrl,
  timeout: config.api.timeout,
  retry: config.api.retry,
});

// ─── Network Monitor ───
export const network = createNetworkMonitor({
  pingUrl: config.network.pingUrl,
  pingInterval: config.network.pingInterval,
});

// ─── Auth Manager (DryAuth full lifecycle) ───
// Uses getToken() for backward compat with existing localStorage token,
// plus refreshToken/onAuthExpired for full lifecycle management.
export const auth = createAuthManager({
  getToken: () => localStorage.getItem(config.auth.storageKey),
  headerName: config.auth.headerName,
  tokenPrefix: config.auth.tokenPrefix,
  refreshBuffer: config.auth.refreshBuffer,
  // NOTE: no `refreshToken` callback — getToken mode never invokes it. The
  // bespoke refresh flow lives in the 401 error interceptor below (offline
  // unlock decrypts the vault's refresh token via secure/reconnect).
  onAuthExpired: () => {
    localStorage.removeItem(config.auth.storageKey);
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    window.location.href = '/login';
  },
});

// Wire auth interceptor into HTTP client
http.addRequestInterceptor(auth.createInterceptor());

// 401 handling: attempt one refresh-then-retry before giving up. The bespoke
// refresh flow (secure/reconnect) decrypts the vault's refresh token — which may
// require an unlock the user hasn't done this session. When the vault isn't
// usable (not enrolled, offline, or refresh rejected), clear the stale session
// and redirect to login (the original behavior).
http.addErrorInterceptor(async (error: any) => {
  const status = error?.status || error?.response?.status;
  const url: string = error?.config?.url ?? '';
  const isAuthCall = url.includes('/auth/');
  const alreadyRetried = error?.config?.meta?.__refreshRetried;

  if (status === 401 && !isAuthCall && !alreadyRetried) {
    const vault = createSecureVault();
    if (vault.isEnrolled() && navigator.onLine) {
      try {
        // getUnlockedRefreshToken resolves the decrypted refresh token, prompting
        // the LockScreen if the key isn't already in memory this session.
        await refreshAccessTokenWith(getUnlockedRefreshToken, (u, b) =>
          http.request({ url: u, method: 'POST', body: b }),
        );
        // Retry the original request once with the new token. The retried config
        // carries __refreshRetried so a second 401 skips this block (no loop).
        const retryConfig = {
          ...error.config,
          meta: { ...(error.config?.meta ?? {}), __refreshRetried: true },
        };
        return http.request(retryConfig);
      } catch {
        // fall through to redirect
      }
    }

    const token = localStorage.getItem(config.auth.storageKey);
    if (token) {
      console.warn('[Auth] Refresh failed/unavailable (401). Clearing session.');
      localStorage.removeItem(config.auth.storageKey);
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      // Clear DryStore cache + sync checkpoints
      import('./database').then(({ destroyDatabase }) => destroyDatabase()).catch(() => {});
      window.location.href = '/login';
    }
  }
  throw error;
});

// ─── WebSocket (DryConnect native, auth-aware) ───
//
// Uses the new DryConnect WS surface (post-update):
//   - `auth: AuthManager` integration: auto-disconnects on token expiry,
//     reconnects when re-authenticated, uses `auth.getValidToken()` to
//     fetch a fresh token before each (re)connect — no more stale-token
//     silent failures.
//   - `ticketParam: 'token'` matches our backend's existing query param.
//   - `autoReconnect: true` is safe now because `jitter: true` (default)
//     spreads reconnect attempts so a backend pod restart doesn't trigger
//     a thundering herd from all open tabs.
//   - `sendQueueSize: 50` (default) buffers outbound messages while
//     disconnected and flushes on reconnect.
//
// Status changes (connecting/connected/disconnected/reconnecting) are
// fanned out through `addStatusListener` so the React WebSocketContext
// can mirror them into component state.
import type { WSStatus, WSStatusListener, WebSocketClient } from '@drystuff/connect';

export type AppWebSocketClient = WebSocketClient & {
  /** Subscribe to status changes. Returns an unsubscribe function. */
  addStatusListener: (cb: WSStatusListener) => () => void;
};

export function createAppWebSocket(_token?: string): AppWebSocketClient | null {
  const baseUrl = config.api.baseUrl;
  // Drop the explicit token from the URL — auth.getValidToken() supplies
  // it via the ticketProvider path. Keeps refresh-flow correctness on
  // every reconnect.
  const wsUrl = baseUrl.replace(/^http/, 'ws').replace('/api/v1', '') + '/api/v1/ws';
  try {
    const statusListeners = new Set<WSStatusListener>();
    const ws = createWebSocket({
      url: wsUrl,
      // The backend WS endpoint authenticates via a `?token=<jwt>` query param
      // (main.go: wsRouter /api/v1/ws). createWebSocket only appends the ticket
      // when given a `ticketProvider` — passing `auth` alone did nothing.
      // getValidToken() refreshes an in-memory token, but that state isn't
      // hydrated on a fresh page load (returns null -> "?token=null" -> 401), so
      // fall back to the stored token, the SAME source the HTTP interceptor uses
      // (config.auth.getToken). `?? ''` avoids ever sending the literal "null".
      ticketProvider: async () =>
        (await auth.getValidToken()) || localStorage.getItem(config.auth.storageKey) || '',
      ticketParam: 'token',
      heartbeatInterval: 30000,
      autoReconnect: true,
      jitter: true,
      onStatus: (s: WSStatus) => {
        statusListeners.forEach((l) => {
          try { l(s); } catch { /* isolate */ }
        });
      },
      onAuthFailure: (err: any) => {
        // Stop auto-reconnecting if auth is irrecoverable. The 401
        // interceptor on `http` will redirect to /login when the user's
        // next action also fails.
        console.warn('[WebSocket] Auth failure — halting reconnect:', err.message);
        return false;
      },
    });

    // Initial connect — fire-and-forget. Auth ticket will be fetched
    // via the auth manager.
    ws.connect().catch((err: any) => {
      console.warn('[WebSocket] Initial connect failed (will retry):', err?.message ?? err);
    });

    return Object.assign(ws, {
      addStatusListener(cb: WSStatusListener) {
        statusListeners.add(cb);
        return () => { statusListeners.delete(cb); };
      },
    });
  } catch {
    console.warn('[WebSocket] Failed to create WebSocket client');
    return null;
  }
}

// ─── Notification Manager Factory (DryNotify) ───
// Created lazily after the notifications collection is available.
export function createAppNotificationManager(collection: any): NotificationManager {
  return createNotificationManager({
    collection,
    maxCount: config.notifications.maxCount,
    maxAge: config.notifications.maxAge,
  });
}

// ─── Cache-through interceptors (DryStore native) ───
export function wireCacheInterceptors(collections: Record<string, any>) {
  // All endpoints now return { data: [...] } consistently — no per-endpoint dataKey needed
  const cacheMapping: CacheMapping = {
    '/assets': { collection: collections.assets },
    '/sites': { collection: collections.sites },
    '/forms': { collection: collections.formTemplates },
    '/asset-types': { collection: collections.assetTypes },
    '/submissions': { collection: collections.formSubmissions },
  };

  // Every GET response is auto-cached to IndexedDB
  http.addResponseInterceptor(createCachingInterceptor(cacheMapping));
  // When offline, serve from IndexedDB transparently
  http.addErrorInterceptor(createOfflineFallbackInterceptor(cacheMapping));
}
